import { useVideoCallState } from "@/stores/videoCallState";
import { Actions, Events } from "@events";
import { EventManager } from "@events";
import WebRTCRoom from "../PureWebRTC/webrtcRoom";

const rootEventEmitter = EventManager.getEventEmitter("root");

rootEventEmitter.on(Events.VideoCallCreated, async (payload) => {
  var rtcId = payload.rtcId;

  if (window.location.href.indexOf(rtcId) === -1) {
    console.warn(`rtcId ${rtcId} not found in url, skipping cefBridge creation`);
    return;
  }

  var videoCallState = useVideoCallState(rtcId);
  const scopedEventEmitter = EventManager.getEventEmitter(rtcId);
  const scopedActionEmitter = EventManager.getActionEmitter(rtcId);

  scopedEventEmitter.on(Events.SignalingMessageReceived, function (data) {
    console.log("SignalingMessageReceived", data);
    if (data.type === "externalAdapter:connectionStateChanged") {
      var remoteRole = videoCallState.role === "agent" ? "user" : "agent";
      console.log("Received connectionStateChanged for role", remoteRole, "with args", data.args);
    }
  });

  if (videoCallState.role === "user") {
    startUserObservers(rtcId, scopedEventEmitter, scopedActionEmitter);
  } else if (videoCallState.role === "agent") {
    startAgentObservers(rtcId, scopedEventEmitter, scopedActionEmitter);
  } else {
    console.warn(`Unknown role ${videoCallState.role} for rtcId ${rtcId}, skipping cefBridge creation`);
  }
});

function startAgentObservers(rtcId, scopedEventEmitter, scopedActionEmitter) {
  console.log("external adapter, video call created", rtcId);

  // Initialize transport if not already active
  if (window.ClientTransport && window.ClientTransport.initialize) {
    window.ClientTransport.initialize(); // Fire and forget
  }

  scopedEventEmitter.on("*", function (...args) {
    console.log("event", rtcId, this.event, ...args);
    const cleanedArgs = cleanArray(args);
    if (window.ClientTransport) {
      window.ClientTransport.send("externalAdapter/invokeEvent", {
        rtcId: rtcId,
        name: this.event,
        args: JSON.stringify(cleanedArgs),
      });

      if (this.event === Events.ConnectionStateChanged) {
        scopedActionEmitter.emit(Actions.SendSignalingMessage, {
          cmd: "send",
          msg: JSON.stringify({
            type: "externalAdapter:connectionStateChanged",
            args: [...cleanedArgs, /* current dateTime */ new Date().toISOString()],
          }),
        });
      }
    }
  });

  scopedActionEmitter.on("*", function (...args) {
    const cleanedArgs = cleanArray(args);
    console.log("action", rtcId, this.event, ...args);
    if (window.ClientTransport) {
      window.ClientTransport.send("externalAdapter/invokeAction", {
        rtcId: rtcId,
        name: this.event,
        args: JSON.stringify(cleanedArgs),
      });
    }
  });
}

function startUserObservers(rtcId, scopedEventEmitter, scopedActionEmitter) {
  console.log("external adapter, video call created", rtcId);

  scopedEventEmitter.on("*", function (...args) {
    console.log("event", rtcId, this.event, ...args);
    const cleanedArgs = cleanArray(args);

    if (this.event === Events.ConnectionStateChanged) {
      scopedActionEmitter.emit(Actions.SendSignalingMessage, {
        cmd: "send",
        msg: JSON.stringify({
          type: "externalAdapter:connectionStateChanged",
          args: [...cleanedArgs, /* current dateTime */ new Date().toISOString()],
        }),
      });
    }
  });

  scopedActionEmitter.on("*", function (...args) {
    console.log("action", rtcId, this.event, ...args);
  });
}

const cleanObject = (obj) => {
  // look for properties that are objects and of type WebRTCRoom and remove them
  const cleanedObj = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];
      if (typeof value === "object" && value !== null) {
        // If it's a WebRTCRoom, skip it
        if (value instanceof WebRTCRoom) {
          continue;
        }
        try {
          // Otherwise, clean the object recursively
          cleanedObj[key] = cleanObject(value);
        } catch (e) {
          cleanedObj[key] = value; // Fallback to original value if error occurs
        }
      } else {
        cleanedObj[key] = value;
      }
    }
  }
  return cleanedObj;
};

const cleanArray = (arr) => {
  return arr.map((item) => {
    if (typeof item === "object") {
      return cleanObject(item);
    }
    return item;
  });
};

function cefBridge(rtcId) {
  const actionEmitter = EventManager.getActionEmitter(rtcId);
  const eventEmitter = EventManager.getEventEmitter(rtcId);
  const state = useVideoCallState(rtcId);
  return {
    sendAction(name, ...args) {
      actionEmitter.emit(name, ...args);
    },
    sendEvent(name, ...args) {
      eventEmitter.emit(name, ...args);
    },
    getState(strKeyPath) {
      const keys = strKeyPath.split(".");
      let value = state;
      for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        value = value[key];
      }
      return value;
    },
  };
}

export default cefBridge;
